import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEvent, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { API_BASE_URL } from '@packages/shared-types';

/**
 * Default HTTP options
 */
const DEFAULT_HTTP_OPTIONS = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
  observe: 'response' as 'response',
};

/**
 * Base API Service
 *
 * Shared HTTP service for all Angular applications.
 * Provides common HTTP methods with built-in error handling.
 *
 * @example
 * ```typescript
 * // In your feature service
 * @Injectable({ providedIn: 'root' })
 * export class ProgramsApiService {
 *   private api = inject(BaseApiService);
 *
 *   getPrograms() {
 *     return this.api.get(PROGRAMS_ENDPOINTS.list);
 *   }
 *
 *   getProgramsWithHeader() {
 *     return this.api.getWithHeaders(PROGRAMS_ENDPOINTS.list, {
 *       'X-Program-Id': 'master_admin'
 *     });
 *   }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class BaseApiService {
  private baseUrl = API_BASE_URL;

  constructor(private httpClient: HttpClient) {}

  /**
   * Get full URL for endpoint
   */
  private getUrl(endpoint: string): string {
    return `${this.baseUrl}${endpoint}`;
  }

  // =========================================================================
  // GET Methods
  // =========================================================================

  /**
   * GET request - returns JSON body directly
   */
  public get<T = any>(endpoint: string, params?: HttpParams | Record<string, any>): Observable<T> {
    let httpParams: HttpParams | undefined;
    if (params) {
      httpParams =
        params instanceof HttpParams
          ? params
          : new HttpParams({ fromObject: params as Record<string, string> });
    }
    return this.httpClient
      .get<T>(this.getUrl(endpoint), { params: httpParams })
      .pipe(catchError(this.handleError));
  }

  /**
   * GET request with full response (includes headers)
   */
  public getFullResponse<T = any>(
    endpoint: string,
    params?: HttpParams | Record<string, any>
  ): Observable<any> {
    let httpParams: HttpParams | undefined;
    if (params) {
      httpParams =
        params instanceof HttpParams
          ? params
          : new HttpParams({ fromObject: params as Record<string, string> });
    }
    return this.httpClient
      .get<T>(this.getUrl(endpoint), { observe: 'response', params: httpParams })
      .pipe(catchError(this.handleError));
  }

  /**
   * GET request with custom headers
   * Use for endpoints requiring specific headers (e.g., X-Program-Id)
   */
  public getWithHeaders<T = any>(
    endpoint: string,
    customHeaders: Record<string, string>,
    params?: HttpParams | Record<string, any>
  ): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',

      ...customHeaders,
    });

    let httpParams: HttpParams | undefined;
    if (params) {
      httpParams =
        params instanceof HttpParams
          ? params
          : new HttpParams({ fromObject: params as Record<string, string> });
    }

    return this.httpClient
      .get<T>(this.getUrl(endpoint), { headers, observe: 'response', params: httpParams })
      .pipe(catchError(this.handleError));
  }

  // =========================================================================
  // POST Methods
  // =========================================================================

  /**
   * POST request
   */
  public post<T = any>(endpoint: string, data: any): Observable<any> {
    return this.httpClient
      .post<T>(this.getUrl(endpoint), data, DEFAULT_HTTP_OPTIONS)
      .pipe(catchError(this.handleError));
  }

  /**
   * POST request with custom headers
   * Use for endpoints requiring specific headers (e.g., X-Program-Id for login)
   */
  public postWithHeaders<T = any>(
    endpoint: string,
    data: any,
    customHeaders: Record<string, string>
  ): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      ...customHeaders,
    });

    return this.httpClient
      .post<T>(this.getUrl(endpoint), data, { headers, observe: 'response' })
      .pipe(catchError(this.handleError));
  }

  /**
   * POST request with full response
   */
  public postFullResponse<T = any>(endpoint: string, data: any): Observable<any> {
    return this.httpClient
      .post<T>(this.getUrl(endpoint), data, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * POST with FormData (file uploads)
   * Note: Do NOT set Content-Type header - browser sets it with boundary
   */
  public postWithFormData<T = any>(
    endpoint: string,
    data: FormData,
    headers: { 'X-Program-Id': string }
  ): Observable<any> {
    const httpHeaders = new HttpHeaders(headers);
    return this.httpClient
      .post<T>(this.getUrl(endpoint), data, { headers: httpHeaders, observe: 'response' })
      .pipe(catchError(this.handleError));
  }

  // =========================================================================
  // PUT Methods
  // =========================================================================

  /**
   * PUT request
   */
  public put<T = any>(endpoint: string, data: any): Observable<any> {
    return this.httpClient
      .put<T>(this.getUrl(endpoint), data, DEFAULT_HTTP_OPTIONS)
      .pipe(catchError(this.handleError));
  }

  /**
   * PUT request with custom headers
   */
  public putWithHeaders<T = any>(
    endpoint: string,
    data: any,
    customHeaders: Record<string, string>
  ): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      ...customHeaders,
    });

    return this.httpClient
      .put<T>(this.getUrl(endpoint), data, { headers, observe: 'response' })
      .pipe(catchError(this.handleError));
  }

  /**
   * PUT with FormData
   * Note: Do NOT set Content-Type header - browser sets it with boundary
   */
  public putWithFormData<T = any>(
    endpoint: string,
    data: FormData,
    headers: { 'X-Program-Id': string }
  ): Observable<any> {
    const httpHeaders = new HttpHeaders(headers);
    return this.httpClient
      .put<T>(this.getUrl(endpoint), data, { headers: httpHeaders, observe: 'response' })
      .pipe(catchError(this.handleError));
  }

  // =========================================================================
  // DELETE Methods
  // =========================================================================

  /**
   * DELETE request
   */
  public delete<T = any>(endpoint: string): Observable<any> {
    return this.httpClient.delete<T>(this.getUrl(endpoint)).pipe(catchError(this.handleError));
  }

  /**
   * DELETE request with custom headers
   */
  public deleteWithHeaders<T = any>(
    endpoint: string,
    customHeaders: Record<string, string>
  ): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      ...customHeaders,
    });

    return this.httpClient
      .delete<T>(this.getUrl(endpoint), { headers, observe: 'response' })
      .pipe(catchError(this.handleError));
  }

  // =========================================================================
  // Upload Methods
  // =========================================================================

  /**
   * Upload with progress tracking
   */
  public upload(
    endpoint: string,
    formData: FormData,
    customHeaders?: Record<string, string>
  ): Observable<HttpEvent<any>> {
    const headers = customHeaders ? new HttpHeaders(customHeaders) : undefined;
    
    return this.httpClient
      .post<any>(this.getUrl(endpoint), formData, {
        reportProgress: true,
        observe: 'events',
        headers,
      })
      .pipe(catchError(this.handleError));
  }

  // =========================================================================
  // Error Handler
  // =========================================================================

  /**
   * Centralized error handler
   * Override this in child class for custom error handling
   */
  protected handleError(error: any): Observable<never> {
    console.error('API Error:', error);
    return throwError(() => error);
  }
}
